import { cn } from '@/lib/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isSameDay } from 'date-fns'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { Input } from '../ui'
import { formatTypedDate, maskTypedDate, parseTypedDate } from './typed-date'

interface Props {
  id?: string
  className?: string
  value: Date | null
  placeholder?: string
  disabled?: boolean
  clearable?: boolean
  /** The button that opens the calendar, rendered inside the field. */
  calendarToggle: ReactNode

  onChange(date: Date | null): void
}

function isSameDate(a: Date | null, b: Date | null) {
  if (!a || !b) return a === b
  return isSameDay(a, b)
}

/**
 * A date typed as `DD.MM.YYYY`, with the calendar one click away. The date is
 * applied as soon as a whole, real one has been typed; anything left half-typed
 * or invalid when the field loses focus falls back to the last good value.
 */
export const TypedDateField: React.FC<Props> = ({
  id,
  className,
  value,
  placeholder,
  disabled = false,
  clearable = false,
  calendarToggle,

  onChange,
}) => {
  const translations = useTranslate({
    placeholder: 'components.date-picker.typed-placeholder',
  })

  const [text, setText] = useState(() => formatTypedDate(value))
  const [shownValue, setShownValue] = useState(value)

  // A date picked in the calendar, or set by the form, replaces the text.
  if (!isSameDate(value, shownValue)) {
    setShownValue(value)
    setText(formatTypedDate(value))
  }

  function handleChange(raw: string) {
    const masked = maskTypedDate(raw)
    setText(masked)

    const parsed = masked.length === 10 ? parseTypedDate(masked) : null
    if (parsed && !isSameDate(parsed, value)) onChange(parsed)
  }

  function commit() {
    if (!text.trim()) {
      if (clearable && value) onChange(null)
      else setText(formatTypedDate(value))
      return
    }

    const parsed = parseTypedDate(text)
    if (parsed && !isSameDate(parsed, value)) {
      onChange(parsed)
      return
    }

    setText(formatTypedDate(value))
  }

  return (
    <div className={cn('relative', className)}>
      <Input
        id={id}
        value={text}
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder ?? translations.placeholder}
        disabled={disabled}
        className="bg-quaternary pr-14"
        onChange={handleChange}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          commit()
        }}
      />
      <div className="absolute top-1/2 right-2 -translate-y-1/2">
        {calendarToggle}
      </div>
    </div>
  )
}
